import React, { useEffect, useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import VolumeUp from '@material-ui/icons/VolumeUp';
import VolumeOff from '@material-ui/icons/VolumeOff';
import GetAppIcon from '@material-ui/icons/GetApp';
import { Slider, CircularProgress } from '@material-ui/core';
import { secondsToTimeString } from 'utils';
import { CourseTheme } from 'assets/themes/Course';

enum enStatus {
  'PAUSED' = 'paused',
  'PLAYING' = 'playing',
  'LOADING' = 'loading',
}

interface IProps {
  src: string;
  enabledDownload: boolean;
}

const useStyles = makeStyles<CourseTheme>((theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    padding: 14,
    paddingRight: 24,
    backgroundColor: theme.palette.grey['200'],
    borderRadius: 8,
    alignItems: 'center',
  },
  bar: {
    flex: 1,
    backgroundColor: '#c1c2c3',
    height: 4,
    borderRadius: 0,
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 1px 10px 0 rgba(0,0,0,0.2)',
    },
  },
  time: {
    color: '#646464',
    fontSize: 12,
    margin: '0 10px',
  },
  btn: {
    color: theme.palette.grey['600'],
    display: 'flex',
    cursor: 'pointer',
    borderRadius: '50%',
    '&:hover': {
      boxShadow: '0 1px 4px 0 rgba(0,0,0,0.2)',
    },
    transition: '0.3s',
  },
  loading: {
    '& > div': {
      maxWidth: 22,
      maxHeight: 22,
      color: theme.palette.grey['600'],
    },
  },
  volume: {
    color: theme.palette.grey['600'],
    display: 'flex',
    margin: '0',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'flex-end',
    transition: '0.3s',
    width: 44,
    padding: '0 10px',
    '& > span': {
      marginRight: 10,
      padding: '10px 0',
      display: 'none',
      color: theme.palette.grey['600'],
    },
    '&:hover': {
      width: 74,
      backgroundColor: '#dbdbdb',
      borderRadius: 20,
      minWidth: 95,
      '& > span': {
        display: 'block',
      },
    },
  },
  barVolume: {
    color: theme.palette.grey['600'],
    width: 40,
    backgroundColor: theme.palette.grey['500'],
    height: 4,
    borderRadius: 4,
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 1px 10px 0 rgba(0,0,0,0.2)',
    },
  },
  download: {
    display: 'flex',
    '& > svg': {
      fill: theme.palette.grey['600'],
    },
  },
  audioTag: {
    width: '100%',
  },

  downloadIconIOS: {
    '& > svg': {
      fill: '#08c',
    },
  },
  customColor: {
    color: theme.palette.grey['600'],
    '& > [role="slider"]:hover, & > [role="slider"]:active': {
      boxShadow: '0px 0px 0px 14px rgba(55, 71, 79, 0.4)',
    },
  },
}));

const AudioPlayer = (props: IProps) => {
  const { src, enabledDownload } = props;
  const audio = React.useRef(new Audio(src) as any);

  const [status, setStatus] = useState(enStatus.LOADING);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(100);
  const [isLoaded, setIsLoaded] = useState(false);
  const classes = useStyles();

  const pause = useCallback(() => {
    audio.current.pause();
    setStatus(enStatus.PAUSED);
  }, []);

  const onAudioEnded = useCallback(() => {
    pause();
    audio.current.currentTime = 0;
    setCurrentTime(0);
  }, [audio, pause]);

  const canPlayThrough = useCallback(() => {
    setDuration(Math.floor(audio.current.duration));
    if (!isLoaded) setIsLoaded(true);
    if (!audio.current.duration || audio.current.paused) {
      setStatus(enStatus.PAUSED);
      return;
    }

    setStatus(enStatus.PLAYING);
  }, [isLoaded]);

  useEffect(() => {
    const audioReference = audio.current;

    audioReference.addEventListener('canplaythrough', canPlayThrough, false);
    audioReference.addEventListener('timeupdate', onTimeUpdated, false);
    audioReference.addEventListener('ended', onAudioEnded, false);

    return () => {
      onAudioEnded();
      audioReference.removeEventListener('timeupdate', onTimeUpdated);
      audioReference.removeEventListener('ended', onAudioEnded);
      audioReference.removeEventListener('canplaythrough', canPlayThrough);
    };
  }, [audio, canPlayThrough, onAudioEnded]);

  const onTimeUpdated = () => {
    setCurrentTime(Math.floor(audio.current.currentTime));
  };
  const play = useCallback(async () => {
    await audio.current.play();
    setStatus(enStatus.PLAYING);
  }, []);

  const handleChangeVolume = useCallback((event: any, newValue: number | number[]) => {
    audio.current.volume = (newValue as number) / 100;
    setVolume(newValue as number);
  }, []);

  const enableVolume = useCallback(() => {
    if (audio.current.volume > 0) {
      audio.current.volume = 0;
      setVolume(0);
      return;
    }
    setVolume(100);
    audio.current.volume = 1;
  }, []);

  const handleChangeCurrentTime = useCallback((event: any, newValue: number | number[]) => {
    setStatus(enStatus.LOADING);
    setCurrentTime(Math.floor(newValue as number));
    audio.current.currentTime = newValue as number;
  }, []);

  const isAppleOS = () =>
    navigator.userAgent.match(/Version\/[\d.]+.*Safari/) || /iPad|iPhone|iPod/.test(navigator.userAgent);

  const button = {
    paused: () => {
      return (
        <div className={classes.btn} onClick={play}>
          <PlayArrowIcon />
        </div>
      );
    },
    playing: () => {
      return (
        <div className={classes.btn} onClick={pause}>
          <PauseIcon />
        </div>
      );
    },
    loading: () => {
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );
    },
  };

  if (!src) return null;

  if (isAppleOS()) {
    if (!audio) {
      return null;
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', color: '' }}>
        <audio ref={audio} controls className={`${classes.audioTag} `}>
          <source src={src} type='audio/mpeg' />
        </audio>
        {enabledDownload && (
          <a href={src} className={classes.downloadIconIOS}>
            <GetAppIcon />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {button[status] && button[status]()}

      <div className={classes.time}>
        {secondsToTimeString(currentTime, duration >= 3600)}&nbsp;/&nbsp;{secondsToTimeString(duration)}
      </div>

      <Slider
        className={classes.customColor}
        disabled={!isLoaded}
        onChange={handleChangeCurrentTime}
        value={currentTime}
        max={duration}
        min={0}
      />

      <div className={classes.volume}>
        <Slider
          className={classes.customColor}
          value={volume}
          onChange={handleChangeVolume}
          aria-labelledby='continuous-slider'
        />
        {!!volume ? (
          <div onClick={enableVolume}>
            <VolumeUp />
          </div>
        ) : (
          <div onClick={enableVolume}>
            <VolumeOff />
          </div>
        )}
      </div>

      {enabledDownload && (
        <a href={src} className={classes.download}>
          <GetAppIcon />
        </a>
      )}
    </div>
  );
};

export default React.memo(AudioPlayer);
